import { isEmpty } from 'lodash'
import queryString from 'query-string'

import { apiService, fetchingAPI } from '@/api'
import { exportCsv } from '@/components/helpers/exportToCsv'
import { toast } from '@/components/toasts/toast.helpers'
import {
  DISMISSED_EXPERIMENT_STATUS,
  INSIGHTS_ERROR,
  PENDING_EXPERIMENT_STATUS,
} from '@/views/AgenticInsights/AgenticInsights.constants'
import {
  formatInsightFromAPI,
  formatInsightsFromAPI,
  formatUserName,
  splitRecommendationIntoDisplayItems,
} from '@/views/AgenticInsights/AgenticInsights.helpers'
import * as redux from './agenticInsights.redux'

// How the trigger rule was composed before the pipeline generated it (TREN-463).
// Kept for insights created before that shipped, so they stay launchable.
const legacyTriggerRule = (insight) =>
  insight.conversionReasonDescription
    ? `Is the customer raising the objection "${insight.conversionReason}": ${insight.conversionReasonDescription}?`
    : `Is the customer raising the objection "${insight.conversionReason}"?`

// The org dropdown lets a Balto admin switch faster than a request completes, so
// every fetch is numbered and only the newest one is allowed to write. Without
// this a slow response for the org you just left lands under the org you just
// picked — the same wrong-org display this action is meant to prevent.
let latestInsightsRequest = 0

// Three separate checks on the endpoint answer 403: the feature flag, ordinary
// org authorization, and the unredacted/support-access rule that applies to org 1
// itself. Only the first means "not enabled", so the message decides — telling an
// admin a feature isn't enabled when it is would send them to flip a flag that's
// already on.
const insightsErrorKind = (error) => {
  if (error?.status !== 403) {
    return INSIGHTS_ERROR.FETCH_FAILED
  }
  return /not enabled/i.test(error?.message ?? '')
    ? INSIGHTS_ERROR.NOT_ENABLED
    : INSIGHTS_ERROR.NOT_PERMITTED
}

export const fetchAgenticInsights = (orgId) => async (dispatch) => {
  const requestId = latestInsightsRequest + 1
  latestInsightsRequest = requestId

  dispatch(redux.setLoading(true))
  // Emptied before the request, not after it fails: otherwise a rejected fetch
  // leaves the previous org's insights in the store and the page renders them as
  // though they belonged to the org just selected.
  dispatch(redux.setAgenticInsights([]))

  try {
    const apiInsights = await fetchingAPI(
      `${apiService.summary}/insights?requested_organization_id=${orgId}`,
      'GET',
      dispatch
    )

    if (requestId === latestInsightsRequest) {
      dispatch(redux.setAgenticInsights(formatInsightsFromAPI(apiInsights)))
    }
  } catch (error) {
    if (requestId !== latestInsightsRequest) {
      return
    }
    const kind = insightsErrorKind(error)
    // A 403 describes a state rather than a failure, so it gets plain copy on the
    // page and no toast.
    if (kind === INSIGHTS_ERROR.FETCH_FAILED) {
      toast.error('Failed to fetch agentic insights', { cause: error })
    }
    dispatch(redux.setError({ kind }))
  } finally {
    if (requestId === latestInsightsRequest) {
      dispatch(redux.setLoading(false))
    }
  }
}

// Whether the org has ever had a run — so the empty state can distinguish
// "we haven't run yet" from "we ran and found nothing." On failure we leave
// hasRuns null and the page falls back to the "found nothing" copy.
export const fetchRunStatus = (orgId) => async (dispatch) => {
  try {
    const response = await fetchingAPI(
      `${apiService.summary}/insights/run-status?requested_organization_id=${orgId}`,
      'GET',
      dispatch
    )
    dispatch(redux.setHasRuns(response?.has_runs ?? null))
  } catch (error) {
    dispatch(redux.setHasRuns(null))
  }
}

export const fetchAgenticInsightById = (insightId) => async (dispatch) => {
  dispatch(redux.setLoading(true))

  try {
    const apiInsight = await fetchingAPI(
      `${apiService.summary}/insights/${insightId}`,
      'GET',
      dispatch
    )

    dispatch(redux.setAgenticInsights([formatInsightFromAPI(apiInsight)]))
  } catch (error) {
    toast.error('Failed to fetch insight', { cause: error })
    dispatch(redux.setError({ kind: INSIGHTS_ERROR.FETCH_FAILED }))
  } finally {
    dispatch(redux.setLoading(false))
  }
}

export const createDynamicPromptFromInsight =
  (insight, { displayItems, categoryName, playbookCids = [] } = {}) =>
  async (dispatch) => {
    // Passed in by the caller, which got them from the scope endpoint: the
    // playbooks derived from this opportunity's sampled calls, rather than the
    // ones the run was configured with. An org-wide run has none of the latter,
    // which used to leave the prompt with nowhere to go.
    if (isEmpty(playbookCids)) {
      toast.error('No playbooks are associated with this insight')
      return false
    }

    dispatch(redux.setCreatingPromptForInsight(insight.id))

    try {
      // Split the recommendation into one checklist item per sentence so the
      // dynamic prompt reads as discrete, checkable agent steps rather than one
      // dense blob. An explicit displayItems (e.g. user-edited in a preview)
      // is respected as-is.
      const resolvedDisplayItems =
        displayItems ?? splitRecommendationIntoDisplayItems(insight.recommendation)
      const resolvedCategoryName = categoryName ?? insight.conversionReason
      // prompt_text stays independent of the user-editable categoryName so
      // detection doesn't drift when someone renames the card.
      const body = JSON.stringify({
        organization_id: insight.organizationId,
        insight_id: insight.id,
        playbook_cids: playbookCids,
        category_name: resolvedCategoryName,
        prompt_text: insight.guidanceTriggerPrompt || legacyTriggerRule(insight),
        display_items: resolvedDisplayItems,
      })

      const response = await fetchingAPI(
        `${apiService.core}/api/playbooks/create_dynamic_prompt_from_insight`,
        'POST',
        dispatch,
        body
      )

      const count = response?.results?.length ?? 0
      dispatch(redux.setInsightPromptCreated(insight.id))
      toast.success(`Dynamic prompt created in ${count} playbook${count === 1 ? '' : 's'}`)
      return true
    } catch (error) {
      toast.error(error?.detail || error?.message || 'Failed to create dynamic prompt')
      return false
    } finally {
      dispatch(redux.setCreatingPromptForInsight(null))
    }
  }

// Download the run's classified calls as a CSV. The backend returns the CSV
// text (call metadata + classifications only); exportCsv triggers the browser
// download.
export const exportRunCallsCsv = (runId) => async (dispatch) => {
  try {
    const response = await fetchingAPI(
      `${apiService.summary}/insights/runs/${runId}/export`,
      'GET',
      dispatch
    )

    if (!response?.csv) {
      toast.error('No calls to export for this run')
      return
    }

    exportCsv(response.csv, null, 'call breakdown')
    toast.success(`Exported ${response.row_count.toLocaleString()} calls`)
  } catch (error) {
    toast.error(error?.message || 'Failed to export calls')
  }
}

export const markInsightReviewed = (insightId) => async (dispatch) => {
  try {
    const reviewedUsers = await fetchingAPI(
      `${apiService.summary}/insights/${insightId}/reviewed-users`,
      'PATCH',
      dispatch
    )

    const people = (reviewedUsers || []).map(
      (user) => [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username
    )

    dispatch(redux.setInsightReviewedUsers({ insightId, people }))
  } catch (error) {
    // Silent failure - marking as reviewed is not critical
  }
}

export const activateCoachingFromInsight = (insightId, orgId) => async (dispatch) => {
  dispatch(redux.setActivatingInsight(insightId))

  try {
    const body = JSON.stringify({
      insight_id: insightId,
      organization_id: orgId,
    })

    const response = await fetchingAPI(
      `${apiService.summary}/coaching/activate-from-insight`,
      'POST',
      dispatch,
      body
    )

    toast.success(`AI Coach activated for ${response.agents_coached} agent(s)`)
    return true
  } catch (error) {
    toast.error(error?.message || 'Failed to activate AI Coach')
    return false
  } finally {
    dispatch(redux.setActivatingInsight(null))
  }
}

// Starts (or confirms) the insight's experiment. The measurement window and
// started_by are owned by this one call, so a launch tracks the experiment the
// same way regardless of which interventions were chosen. Idempotent
// server-side, so it's safe even when coaching activation already started it.
export const startInsightExperiment = (insightId) => async (dispatch) => {
  try {
    const response = await fetchingAPI(
      `${apiService.summary}/insights/${insightId}/experiment-status`,
      'PATCH',
      dispatch,
      JSON.stringify({ experiment_status: 'running' })
    )

    dispatch(
      redux.setInsightExperiment({
        insightId,
        experimentStatus: response.experiment_status,
      })
    )
    return true
  } catch (error) {
    toast.error(error?.message || 'Failed to start the experiment')
    return false
  }
}

const setInsightDismissal = (insightId, experimentStatus) => async (dispatch) => {
  try {
    const response = await fetchingAPI(
      `${apiService.summary}/insights/${insightId}/experiment-status`,
      'PATCH',
      dispatch,
      JSON.stringify({ experiment_status: experimentStatus })
    )

    dispatch(
      redux.setInsightExperiment({
        insightId,
        experimentStatus: response.experiment_status,
      })
    )
    return true
  } catch (error) {
    toast.error(error?.message || 'Failed to update the insight')
    return false
  }
}

// Dismissal is stored on the insight, so it hides the card for the whole org,
// not just this browser. Undo sets it back to pending.
export const dismissInsight = (insightId) => async (dispatch) => {
  const dismissed = await dispatch(setInsightDismissal(insightId, DISMISSED_EXPERIMENT_STATUS))
  if (dismissed) {
    toast('Insight dismissed', {
      action: {
        label: 'Undo',
        onClick: () => dispatch(setInsightDismissal(insightId, PENDING_EXPERIMENT_STATUS)),
      },
    })
  }
}

// An agent can only be coached on one thing at a time, so a running experiment
// holds its agents. Asked before offering Launch, so the panel can explain
// rather than the user finding out after committing.
export const fetchInsightCoachingConflicts = (insightId, orgId) => async (dispatch) => {
  try {
    const response = await fetchingAPI(
      `${apiService.summary}/coaching/insight-coaching-conflicts/${insightId}?${queryString.stringify(
        { requested_organization_id: orgId }
      )}`,
      'GET',
      dispatch
    )

    return {
      totalAgents: response.total_agents,
      conflictingAgents: response.conflicting_agents,
      blockingExperiments: response.blocking_experiments,
      blockedUntil: response.blocked_until,
      agents: (response.agents || []).map(formatUserName),
      guidancePlaybookCids: response.guidance_playbook_cids || [],
    }
  } catch (error) {
    // Advisory only — the launch enforces this server-side regardless. Failing
    // quietly beats an error toast on a panel the user only opened to read.
    return null
  }
}

// One launch action: apply the chosen interventions, then start the single
// experiment.
//
// Coaching goes first deliberately. It's the step that can be refused (a
// running experiment holding an agent), and the dynamic prompt is written into
// the playbook as a new config version that we can't take back — so creating
// the prompt first would leave it stranded behind a coaching failure. This way
// a refused launch leaves nothing behind.
export const launchInsightExperiment =
  (insight, { realtime, coaching, playbookCids = [] }) =>
  async (dispatch) => {
    dispatch(redux.setLaunchingInsight(insight.id))

    try {
      const coachingApplied =
        coaching &&
        (await dispatch(activateCoachingFromInsight(insight.id, insight.organizationId)))
      if (coaching && !coachingApplied) {
        // activateCoachingFromInsight has already explained why. Nothing was
        // created, so there's nothing to start and nothing to clean up.
        return
      }

      const realtimeApplied =
        realtime && (await dispatch(createDynamicPromptFromInsight(insight, { playbookCids })))

      if (!coachingApplied && !realtimeApplied) {
        return
      }

      const started = await dispatch(startInsightExperiment(insight.id))
      if (!started) {
        return
      }

      if (realtime && !realtimeApplied) {
        // Coaching is live and the experiment is measuring, but the guidance
        // they asked for isn't there. Say so rather than reporting a clean win.
        toast.warning('Experiment launched with coaching only — real-time guidance failed')
        return
      }
      toast.success('Experiment launched — measuring for 30 days')
    } finally {
      dispatch(redux.setLaunchingInsight(null))
    }
  }
